import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { AppConfig } from "@/app-config";
import Confirmation from "@/views/Confirmation.vue";
import EditBucket from "@/views/EditBucket.vue";
import BucketItem from "@/components/BucketItem.vue";
import Loading from "@/components/Loading.vue";
import { ApiResult } from "../../enums";
import { Consts } from '@/consts';
import { GraphApiUtils } from '@/utils/graphApiUtils';
import { PathUtils } from '@/utils/pathUtils';
import { Plan } from '@/models/plan';
import { PlannerUtils } from '@/utils/plannerUtils';
import { Buckets } from '@/models/buckets';
import { Tasks } from '@/models/tasks';
import { DateUtils } from '@/utils/dateUtils';
import { TaskPatch, Task, TaskPost } from '@/models/task';
import { Users } from '@/models/users';
import { GroupUtils } from '@/utils/groupUtils';
import { Channel } from '@/models/channel';
import { Bucket } from '@/models/bucket';
import { Channels } from '@/models/channels';
import { Plans } from '@/models/plans';

@Component({
	components: { Confirmation, EditBucket, Loading, BucketItem }
})
export default class AdminChannel extends Vue {
	@Prop() public channels!: Channels;
	@Prop() public plans!: Plans;

	private channel: Channel | null = null;
	private plan: Plan | null = null;
	private editBucketVisible: boolean = false;
	private editBucketId: string = '';
	private editBucketTitle: string = '';
	private editBucketDate: string | null = null;
	private editBucketDateTitle: string = '';
	private confirmationVisible: boolean = false;
	private subTitle: string = "カリキュラム編集";
	private itemListTitle: string = "登録内容";
	private confirmationTitle: string = "削除しますか？";
	private confirmationMessage: string = '';
	private uploadPath: string = '';
	private loadingBucket: boolean = true;
	private shouldShowBucket: boolean = true;

	private users: Users = new Users();
	private buckets: Buckets = new Buckets();
	private tasks: Tasks = new Tasks();

	public async created(): Promise<void> {
		// アクセストークンが有効でない場合はルートに遷移。
		if (!await GraphApiUtils.isValidAccessToken(Vue.$accessTokenInfo)) {
			this.$router.push(Consts.PATH_USER);
			return;
		}
		await this.update();
	}
	// TODO channelsの更新とplansの更新が同時に行われていることを前提にしているこの処理はNG
	@Watch('channels')
	private async onChannelsChange(newChannels: Channels, oldChannels: Channels): Promise<void> {
		await this.update();
	}

	private async update(): Promise<void> {
		this.users = new Users();
		this.buckets = new Buckets();
		this.tasks = new Tasks();
		const groupId: string | null = localStorage.getItem(Consts.LOCAL_STORAGE_KEY_USER_GROUPID);
		if (groupId === null) {
			return;
		}
		const channelId: string = this.$route.params.id;
		const channel: Channel | null = this.getChannel(channelId);
		if (channel === null) {
			return;
		}
		const plan: Plan | null = this.getPlan(channel.displayName);
		if (plan === null) {
			return;
		}
		const accessToken: string = Vue.$accessTokenInfo.token;
		const users: Users | null = await this.getUsers(accessToken, groupId);
		if (users === null) {
			return;
		}
		const buckets: Buckets | null = await this.getBuckets(accessToken, plan.id);
		if (buckets === null) {
			return;
		}
		const tasks: Tasks | null = await this.getTasks(accessToken, plan.id);
		if (tasks === null) {
			return;
		}
		this.channel = channel;
		this.plan = plan;
		this.users = users;
		this.tasks = tasks; // buckets以前に設定しておく
		this.buckets = buckets;
		this.loadingBucket = false;
	}

	private getBucketItemDateString(bucketId: string, bucketName: string): string {
		if ((this.plan === null) || (this.tasks === null)) {
			return '';
		}
		const bucketTasks: Tasks = this.tasks.getBucketTasks(this.plan.id, bucketId);
		switch (bucketName) {
			case AppConfig.BUCKET_NAME_PRETASK:
			case AppConfig.BUCKET_NAME_PRETASK2:
			case AppConfig.BUCKET_NAME_PRETASK3:
			case AppConfig.BUCKET_NAME_POSTTASK: {
				const task = bucketTasks.getByTitle(AppConfig.ADMIN_TASK_NAME_SCORE_REPORT);
				if ((task === null) || (task.startDateTime === null)) {
					return '';
				}
				const startDateTime: Date = new Date(task.startDateTime.getTime());
				startDateTime.setDate(startDateTime.getDate() - 1);
				const formattedDate: string = DateUtils.GetStringWithSlash(startDateTime);
				const date: string = `提出期限：${formattedDate}`;
				return date;
			}
			case AppConfig.BUCKET_NAME_LECTURE: {
				const task = bucketTasks.getByTitle(AppConfig.ADMIN_TASK_NAME_GIVE_LECTURE);
				if ((task === null) || (task.dueDateTime === null)) {
					return '';
				}
				const formattedDate: string = DateUtils.GetStringWithSlash(task.dueDateTime);
				const date: string = `講義日：${formattedDate}`;
				return date;
			}
		}
		return '';
	}

	private getBucketItemDisabled(bucketId: string, bucketName: string): boolean {
		if ((this.plan === null) || (this.tasks === null)) {
			return true;
		}
		const bucketTasks: Tasks = this.tasks.getBucketTasks(this.plan.id, bucketId);
		switch (bucketName) {
			case AppConfig.BUCKET_NAME_PRETASK:
			case AppConfig.BUCKET_NAME_PRETASK2:
			case AppConfig.BUCKET_NAME_PRETASK3:
			case AppConfig.BUCKET_NAME_POSTTASK: {
				const task = bucketTasks.getByTitle(AppConfig.ADMIN_TASK_NAME_SCORE_REPORT);
				if ((task === null) || (task.startDateTime === null)) {
					return true;
				}
				return false;
			}
			case AppConfig.BUCKET_NAME_LECTURE: {
				const task = bucketTasks.getByTitle(AppConfig.ADMIN_TASK_NAME_GIVE_LECTURE);
				if ((task === null) || (task.dueDateTime === null)) {
					return true;
				}
				return false;
			}
		}
		return true;
	}

	private getChannel(channelId: string): Channel | null {
		if (this.channels === undefined) {
			return null;
		}
		const channel: Channel | null = this.channels.get(channelId);
		return channel;
	}

	private getPlan(channelName: string): Plan | null {
		if (this.plans === undefined) {
			return null;
		}
		const plan: Plan | null = this.plans.getByTitle(channelName);
		return plan;
	}

	private async getUsers(accessToken: string, groupId: string): Promise<Users | null> {
		const usersResult = await GroupUtils.GetBothMembers(accessToken, groupId, AppConfig.USER_SECUTIRY_GROUP_ID);
		if ((usersResult.result !== ApiResult.Success) || (usersResult.value === null)) {
			alert('生徒一覧の取得に失敗しました');
			return null;
		}
		const users = usersResult.value;
		return users;
	}

	private async getBuckets(accessToken: string, planId: string): Promise<Buckets | null> {
		const bucketsResult = await PlannerUtils.GetBuckets(accessToken, planId);
		if ((bucketsResult.result !== ApiResult.Success) || (bucketsResult.value === null)) {
			alert('バケット一覧の取得に失敗しました');
			return null;
		}
		const buckets: Buckets = bucketsResult.value;
		buckets.sort();
		return buckets;
	}

	private async getTasks(accessToken: string, planId: string): Promise<Tasks | null> {
		const tasksResult = await PlannerUtils.GetTasks(accessToken, planId);
		if ((tasksResult.result !== ApiResult.Success) || (tasksResult.value === null)) {
			alert('タスク一覧の取得に失敗しました');
			return null;
		}
		const tasks: Tasks | null = tasksResult.value;
		return tasks;
	}

	private onBucketItemClick(bucketId: string): void {
		const bucket: Bucket | null = this.buckets.get(bucketId);
		if (bucket === null) {
			return;
		}
		if ((this.channel === null) || (this.plan === null)) {
			return;
		}
		let dateTitle: string = '';
		switch (bucket.name) {
			case AppConfig.BUCKET_NAME_PRETASK:
			case AppConfig.BUCKET_NAME_PRETASK2:
			case AppConfig.BUCKET_NAME_PRETASK3:
			case AppConfig.BUCKET_NAME_POSTTASK: {
				dateTitle = '提出期限';
				const task = this.tasks.getBucketTaskByTitle(this.plan.id, bucketId, AppConfig.ADMIN_TASK_NAME_SCORE_REPORT);
				if ((task !== null) && (task.startDateTime !== null)) {
					const startDateTime = new Date(task.startDateTime.getTime());
					startDateTime.setDate(startDateTime.getDate() - 1);
					this.editBucketDate = DateUtils.GetStringWithHyphen(startDateTime);
				} else {
					this.editBucketDate = null;
				}
				break;
			}
			case AppConfig.BUCKET_NAME_LECTURE: {
				dateTitle = '講義日';
				const task = this.tasks.getBucketTaskByTitle(this.plan.id, bucketId, AppConfig.ADMIN_TASK_NAME_GIVE_LECTURE);
				if ((task !== null) && (task.dueDateTime !== null)) {
					this.editBucketDate = DateUtils.GetStringWithHyphen(task.dueDateTime);
				} else {
					this.editBucketDate = null;
				}
				break;
			}
		}
		this.uploadPath = PathUtils.GetAdminBucketPath(this.channel.id, bucket.name);
		this.editBucketId = bucketId;
		this.editBucketTitle = `${bucket.name} の編集`;
		this.editBucketDateTitle = dateTitle;
		this.editBucketVisible = true;
	}

	private async onEditBucketCloseClick(bucketId: string, taskDate: string): Promise<void> {
		// バケット編集画面を閉じるボタンを押した場合
		this.editBucketVisible = false;
		const bucket: Bucket | null = this.buckets.get(bucketId);
		if (bucket === null) {
			return;
		}
		if (this.plan === null) {
			return;
		}
		this.loadingBucket = true;
		this.shouldShowBucket = false;
		const accessToken: string = Vue.$accessTokenInfo.token;
		await this.postOrPatchTask(accessToken, this.tasks, this.plan.id, bucket, taskDate);
		this.buckets = new Buckets();
		this.tasks = new Tasks();
		const buckets: Buckets | null = await this.getBuckets(accessToken, this.plan.id);
		if (buckets === null) {
			return;
		}
		const tasks: Tasks | null = await this.getTasks(accessToken, this.plan.id);
		if (tasks === null) {
			return;
		}
		this.buckets = buckets;
		this.tasks = tasks;
		this.loadingBucket = false;
		this.shouldShowBucket = true;
	}

	private onEditBucketClearDateClick(): void {
		this.editBucketDate = null;
	}

	private async postOrPatchTask(
		accessToken: string,
		tasks: Tasks,
		planId: string,
		bucket: Bucket,
		taskDate: string
	): Promise<void> {
		switch (bucket.name) {
			case AppConfig.BUCKET_NAME_PRETASK:
			case AppConfig.BUCKET_NAME_PRETASK2:
			case AppConfig.BUCKET_NAME_PRETASK3:
			case AppConfig.BUCKET_NAME_POSTTASK: {
				// 生徒タスク
				let day: Date = new Date();
				if (taskDate !== '') {
					// 19時指定と+1日してGraph APIの時刻値と合わせる
					day = new Date(taskDate);
					day.setHours(19);
				}
				const usersTasks = tasks.getBucketTasksByTitle(
					planId,
					bucket.id,
					AppConfig.USER_TASK_NAME_SUBMIT_REPORT
				);
				for (const user of this.users.value) {
					const userTask: Task | null = usersTasks.getMyTask(user.id);
					if (userTask === null) {
						// レポート採点タスクを作成する
						const taskPost = new TaskPost();
						taskPost.planId = planId;
						taskPost.bucketId = bucket.id;
						taskPost.title = AppConfig.USER_TASK_NAME_SUBMIT_REPORT;
						taskPost.assignmentsUserId = user.id;
						if (taskDate === '') {
							await PlannerUtils.PostTask(accessToken, taskPost);
						} else {
							taskPost.dueDateTime = day;
							await PlannerUtils.PostTask(accessToken, taskPost);
						}
					} else {
						// レポート提出タスクを更新する
						const taskPatch = new TaskPatch();
						taskPatch.dueDateTime = (taskDate !== '') ? day : null;
						if ((userTask.dueDateTime === null) && (taskPatch.dueDateTime === null)) {
							// スキップ
						} else if ((userTask.dueDateTime !== null) && (taskPatch.dueDateTime !== null)) {
							if (DateUtils.IsSame(userTask.dueDateTime, taskPatch.dueDateTime)) {
								// 日付が同じならスキップ
							} else {
								await PlannerUtils.PatchTask(accessToken, userTask.id, taskPatch, userTask.etag);
							}
						} else {
							await PlannerUtils.PatchTask(accessToken, userTask.id, taskPatch, userTask.etag);
						}
					}
				}
				// 運営タスク
				const adminTask = tasks.getBucketTaskByTitle(
					planId,
					bucket.id,
					AppConfig.ADMIN_TASK_NAME_SCORE_REPORT
				);
				let nextDay: Date = new Date();
				if (taskDate !== '') {
					// 19時指定と+1日してGraph APIの時刻値と合わせる
					nextDay = new Date(taskDate);
					nextDay.setHours(19);
					nextDay.setDate(nextDay.getDate() + 1);
				}
				if (adminTask === null) {
					// レポート採点タスクを作成する
					const taskPost = new TaskPost();
					taskPost.planId = planId;
					taskPost.bucketId = bucket.id;
					taskPost.title = AppConfig.ADMIN_TASK_NAME_SCORE_REPORT;
					if (taskDate === '') {
						await PlannerUtils.PostTask(accessToken, taskPost);
					} else {
						taskPost.startDateTime = nextDay;
						await PlannerUtils.PostTask(accessToken, taskPost);
					}
				} else {
					const taskPatch = new TaskPatch();
					taskPatch.startDateTime = (taskDate !== '') ? nextDay : null;
					if ((adminTask.startDateTime === null) && (taskPatch.startDateTime === null)) {
						// スキップ
					} else if ((adminTask.startDateTime !== null) && (taskPatch.startDateTime !== null)) {
						if (DateUtils.IsSame(adminTask.startDateTime, taskPatch.startDateTime)) {
							// 日付が同じならスキップ
						} else {
							await PlannerUtils.PatchTask(accessToken, adminTask.id, taskPatch, adminTask.etag);
						}
					} else {
						await PlannerUtils.PatchTask(accessToken, adminTask.id, taskPatch, adminTask.etag);
					}
				}
				break;
			}
			case AppConfig.BUCKET_NAME_LECTURE: {
				const adminTask: Task | null = tasks.getBucketTaskByTitle(
					planId,
					bucket.id,
					AppConfig.ADMIN_TASK_NAME_GIVE_LECTURE
				);
				let day: Date = new Date();
				if (taskDate !== '') {
					// 19時指定してGraph APIの時刻値と合わせる
					day = new Date(taskDate);
					day.setHours(19);
				}
				if (adminTask === null) {
					// 講義開催タスクを作成する
					const taskPost = new TaskPost();
					taskPost.planId = planId;
					taskPost.bucketId = bucket.id;
					taskPost.title = AppConfig.ADMIN_TASK_NAME_GIVE_LECTURE;
					if (taskDate === '') {
						await PlannerUtils.PostTask(accessToken, taskPost);
					} else {
						taskPost.startDateTime = day;
						taskPost.dueDateTime = day;
						await PlannerUtils.PostTask(accessToken, taskPost);
					}
				} else {
					// 講義開催タスクを更新する
					const taskPatch = new TaskPatch();
					taskPatch.startDateTime = (taskDate !== '') ? day : null;
					taskPatch.dueDateTime = (taskDate !== '') ? day : null;
					if ((adminTask.dueDateTime === null) && (taskPatch.dueDateTime === null)) {
						// スキップ
					} else if ((adminTask.dueDateTime !== null) && (taskPatch.dueDateTime !== null)) {
						if (DateUtils.IsSame(adminTask.dueDateTime, taskPatch.dueDateTime)) {
							// 日付が同じならスキップ
						} else {
							await PlannerUtils.PatchTask(accessToken, adminTask.id, taskPatch, adminTask.etag);
						}
					} else {
						await PlannerUtils.PatchTask(accessToken, adminTask.id, taskPatch, adminTask.etag);
					}
				}
				break;
			}
		}
	}

	private onConfirmationOkClick(): void {
		// 削除ボタンを押した場合
		this.confirmationVisible = false;
	}

	private onConfirmationCancelClick(): void {
		// 削除をキャンセルボタンを押した場合
		this.confirmationVisible = false;
	}
}
